import { canonicalEquationPropertySpec, canonicalEquationSpecs } from "./equationSpecs.js";

export const provenanceVersion = "strategy-equation-provenance-0.1.0";

export const evidenceClasses = {
  LOCAL_COUNTABLE_SPOT_CHECKED: "LOCAL_COUNTABLE_SPOT_CHECKED",
  SOURCE_ANCHOR_ONLY: "SOURCE_ANCHOR_ONLY",
  LICENSED_ANCHOR_ONLY: "LICENSED_ANCHOR_ONLY",
  RECOVERED_OPERATOR_MODEL: "RECOVERED_OPERATOR_MODEL",
  MIXED_RESTRICTED: "MIXED_RESTRICTED"
};

export const equationPropertySpecs = Object.fromEntries(
  canonicalEquationSpecs.map((spec) => [spec.id, canonicalEquationPropertySpec(spec.id)])
);

export const equationProvenance = [
  {
    equationId: "master-strategic-viability",
    evidenceClass: evidenceClasses.MIXED_RESTRICTED,
    custodySourceIds: [
      "pg-sunzi-giles-1910",
      "pg-prince-machiavelli-marriott-1908",
      "pg-meditations-marcus-aurelius",
      "pg-leviathan-hobbes-1651",
      "recovered-glyph-cleary-audit"
    ],
    spotCheckIds: [
      "sunzi-calculations-victory",
      "sunzi-advantage-disadvantage-balance",
      "prince-prudence-least-dangerous",
      "meditations-opinion-safety",
      "leviathan-covenants-sword"
    ],
    propertySpecId: "master-strategic-viability",
    promotionBlockers: ["needs multi-scenario empirical validation", "Cleary corpus not counted"],
    reviewerBoundary:
      "Master viability is a useful operator model synthesized from recovered research plus local Sunzi anchors; it is not formal proof."
  },
  {
    equationId: "shih-configuration-force",
    evidenceClass: evidenceClasses.MIXED_RESTRICTED,
    custodySourceIds: ["ctext-sunzi", "pg-sunzi-giles-1910", "pg-on-war-clausewitz-graham-1874"],
    spotCheckIds: ["sunzi-ground-victory-relation", "clausewitz-duel-scale"],
    propertySpecId: "shih-configuration-force",
    promotionBlockers: ["needs lawful full-text shih source custody", "needs domain-specific calibration"],
    reviewerBoundary:
      "Shih configuration force is source-anchored as a model of position, timing, coordination, and momentum; it is not a mystical force claim."
  },
  {
    equationId: "no-battle-victory",
    evidenceClass: evidenceClasses.MIXED_RESTRICTED,
    custodySourceIds: ["pg-sunzi-giles-1910", "shambhala-cleary-strategy-guide"],
    spotCheckIds: ["sunzi-advantage-disadvantage-balance"],
    propertySpecId: "no-battle-victory",
    promotionBlockers: ["licensed Cleary source not counted", "requires scenario data"],
    reviewerBoundary:
      "No-battle victory scores preservation and avoided conflict but cannot prove pacifism or universal non-engagement."
  },
  {
    equationId: "timing-window",
    evidenceClass: evidenceClasses.MIXED_RESTRICTED,
    custodySourceIds: ["pg-sunzi-giles-1910", "recovered-glyph-cleary-audit"],
    spotCheckIds: [],
    propertySpecId: "timing-window",
    promotionBlockers: ["requires current facts", "recovered Musashi timing layer not countable"],
    reviewerBoundary:
      "Timing-window output is an operator score over readiness and delay risk; timing cannot be inferred from texts alone."
  },
  {
    equationId: "terrain-position-leverage",
    evidenceClass: evidenceClasses.LOCAL_COUNTABLE_SPOT_CHECKED,
    custodySourceIds: ["pg-sunzi-giles-1910", "pg-on-war-clausewitz-graham-1874", "ctext-sunzi"],
    spotCheckIds: ["sunzi-strong-weak", "sunzi-ground-victory-relation", "clausewitz-duel-scale"],
    propertySpecId: "terrain-position-leverage",
    promotionBlockers: ["needs domain terrain map", "needs live/contextual facts"],
    reviewerBoundary:
      "Terrain leverage is locally spot-checked against Sunzi/Giles but still requires current terrain or market structure data."
  },
  {
    equationId: "information-asymmetry",
    evidenceClass: evidenceClasses.LOCAL_COUNTABLE_SPOT_CHECKED,
    custodySourceIds: ["pg-sunzi-giles-1910", "pg-on-war-clausewitz-graham-1874"],
    spotCheckIds: ["sunzi-spies-information-boundary", "sunzi-strong-weak", "clausewitz-uncertainty-friction"],
    propertySpecId: "information-asymmetry",
    promotionBlockers: ["lawful information-gathering boundary required", "deception backfire needs empirical calibration"],
    reviewerBoundary:
      "Information asymmetry uses lawful visibility and signal-quality variables; it does not authorize unlawful deception or surveillance."
  },
  {
    equationId: "calculations-quality",
    evidenceClass: evidenceClasses.LOCAL_COUNTABLE_SPOT_CHECKED,
    custodySourceIds: ["pg-sunzi-giles-1910", "pg-on-war-clausewitz-graham-1874"],
    spotCheckIds: ["sunzi-calculations-victory", "sunzi-spies-information-boundary", "clausewitz-uncertainty-friction"],
    propertySpecId: "calculations-quality",
    promotionBlockers: ["iteration quality must be measured", "raw calculation count is insufficient"],
    reviewerBoundary:
      "Calculation quality rewards verified improvement per iteration, not volume of analysis or unsupported certainty."
  },
  {
    equationId: "guiguzi-influence-fit",
    evidenceClass: evidenceClasses.SOURCE_ANCHOR_ONLY,
    custodySourceIds: ["ctext-guiguzi", "pg-prince-machiavelli-marriott-1908"],
    spotCheckIds: ["prince-feared-loved-boundary"],
    propertySpecId: "guiguzi-influence-fit",
    promotionBlockers: ["terms review required", "ethics and lawful communication gates required"],
    reviewerBoundary:
      "Guiguzi influence fit is an anchor-only operator model and must not become a manipulation engine."
  },
  {
    equationId: "i-ching-change-kernel",
    evidenceClass: evidenceClasses.LICENSED_ANCHOR_ONLY,
    custodySourceIds: ["ctext-i-ching", "shambhala-cleary-i-ching-guide"],
    spotCheckIds: [],
    propertySpecId: "i-ching-change-kernel",
    promotionBlockers: ["source terms review needed", "oracle or prediction claims blocked"],
    reviewerBoundary:
      "I Ching change kernel is a pattern/state-transition model only, not supernatural prediction."
  },
  {
    equationId: "musashi-yagyu-execution-clarity",
    evidenceClass: evidenceClasses.LICENSED_ANCHOR_ONLY,
    custodySourceIds: ["shambhala-cleary-strategy-guide"],
    spotCheckIds: [],
    propertySpecId: "musashi-yagyu-execution-clarity",
    promotionBlockers: ["licensed text custody needed", "martial strategy source not counted"],
    reviewerBoundary:
      "Musashi/Yagyu clarity is licensed-anchor only until lawful text custody exists."
  },
  {
    equationId: "zen-attention-kernel",
    evidenceClass: evidenceClasses.MIXED_RESTRICTED,
    custodySourceIds: ["pg-meditations-marcus-aurelius", "recovered-glyph-cleary-audit"],
    spotCheckIds: ["meditations-opinion-safety"],
    propertySpecId: "zen-attention-kernel",
    promotionBlockers: ["source review needed", "medical and spiritual attainment claims blocked"],
    reviewerBoundary:
      "Zen attention kernel is a recovered operator model for attention quality, not medical guidance or spiritual proof."
  },
  {
    equationId: "governance-fa-standards",
    evidenceClass: evidenceClasses.MIXED_RESTRICTED,
    custodySourceIds: [
      "ctext-seven-military-classics",
      "pg-bushido-nitobe-1908",
      "pg-prince-machiavelli-marriott-1908",
      "pg-leviathan-hobbes-1651"
    ],
    spotCheckIds: ["bushido-rectitude-foundation", "prince-prudence-least-dangerous", "leviathan-covenants-sword"],
    propertySpecId: "governance-fa-standards",
    promotionBlockers: ["fa/legalist source custody needed", "ethics review required"],
    reviewerBoundary:
      "Governance standards model auditability and role fit; it does not endorse authoritarian politics."
  }
];

export function provenanceByEquationId(equationId) {
  return equationProvenance.find((entry) => entry.equationId === equationId);
}

export function provenanceSummary(entries = equationProvenance) {
  return entries.reduce(
    (summary, entry) => {
      summary.total += 1;
      summary[entry.evidenceClass] = (summary[entry.evidenceClass] ?? 0) + 1;
      if (entry.spotCheckIds.length > 0) summary.spotChecked += 1;
      return summary;
    },
    { total: 0, spotChecked: 0 }
  );
}
